import { Badge, Card, Group, Table, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCurrencyDollar } from '@tabler/icons-react';
import { Claim, Debit } from 'interfaces';

type ClaimTariff = Claim['tariffs'][number];

const getTariffEOBs = (debits: Debit[], tariffID: string) =>
  debits
    .filter((debit) => debit.tariffID === tariffID)
    .flatMap((debit) => debit.eobs ?? []);

const EOBBadges = ({ debits, tariffID }: { debits: Debit[]; tariffID: string }) => {
  const eobs = getTariffEOBs(debits, tariffID);

  if (!eobs.length) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={4} wrap="wrap">
      {eobs.map((eob) => (
        <Tooltip
          key={eob.id ?? eob.code}
          label={eob.description?.toLowerCase()}
          disabled={!eob.description}
          position="top"
          withArrow
          multiline
          maw={320}
        >
          <Badge variant="light" color="red" radius="sm">
            {eob.code}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
};

export const MatchedTariffCard = ({
  tariffs,
  debits = [],
}: {
  tariffs: ClaimTariff[];
  debits?: Debit[];
}) => {
  if (!tariffs.length) return null;
  return (
    <Card withBorder shadow="xs" radius="md" p="md" style={{ opacity: 0.7 }}>
      <Group justify="space-between" mb="xs">
        <Badge color="green" leftSection={<IconCheck size={14} />}>
          Matched
        </Badge>
      </Group>
      <Table
        horizontalSpacing="md"
        verticalSpacing="xs"
        withTableBorder
        withColumnBorders
        striped
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Code</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>EOB</Table.Th>
            <Table.Th ta="right">Rate</Table.Th>
            <Table.Th ta="right">Units</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tariffs.map((t) => (
            <Table.Tr key={`matched-${t.id}`}>
              <Table.Td>
                <Text fw={700} size="sm" style={{ whiteSpace: 'nowrap' }}>
                  {t.code}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge variant="light">{t.type}</Badge>
              </Table.Td>
              <Table.Td style={{ maxWidth: 360 }}>
                <Text
                  size="sm"
                  c="dimmed"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.description}
                </Text>
              </Table.Td>
              <Table.Td>
                <EOBBadges debits={debits} tariffID={t.id} />
              </Table.Td>
              <Table.Td>
                <Group gap={6} justify="flex-end">
                  <IconCurrencyDollar size={16} />
                  <Text size="sm">{Number(t.rate || 0).toFixed(2)}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ta="right">
                  {t.units ?? 1}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
};
